import React, { useState, useEffect } from "react";
import {Box, Collapsible, Heading, Text} from "grommet";
import Loading from "./Loading";
import FontAwesomeIcon from "./FontAwesomeIcon";
import { kebabCase, snakeCase } from "../utils/string";
import { useRouter } from "next/router";
import {
    faLinkSimple as IconLink,
    faChevronDown as IconArrowDown
} from '@fortawesome/pro-light-svg-icons';
import Button from "./Button";

const Section = ({
    title,
    level = 2,
    anchorId,
    children,
    control,
    headerProps,
    contentProps,
    controlProps,
    titleProps,
    fullWidthControls,
    subheader,
    icon, // streamline icon
    iconProps,
    customIcon,
    isLoading,
    hideBorder=false,
    leftControl,
    titleContainerProps,
    subheaderProps,
    collapsible,
    collapsedByDefault=false,
    hideDeepLink=false,
    ...rest
}) => {
    const [isCollapsed, setIsCollapsed] = useState(collapsedByDefault);
    const [showCopyLink, setShowCopyLink] = useState(false);
    const [isLinkCopied, setIsLinkCopied] = useState(false);
    const autoGeneratedId = (typeof title == 'string') ? kebabCase(title?.toString() || '') : null;
    const router = useRouter();

    useEffect(() => {
        if (typeof window !== "undefined") {
            const path = window.location.hash;
            if (path && path.includes('#')) {
                const id = path.replace('#', '');
                if (id) {
                    let d = document.getElementById(id);
                    d?.scrollIntoView({behavior: 'smooth'})
                }
            }
        }
    },[]);

    const copyLink = async () => {
        const link = `${window.location.origin}${router.asPath.split("#")[0]}#${autoGeneratedId}`;
        await navigator.clipboard.writeText(link);
        setIsLinkCopied(true);
        setTimeout(() => {
            setIsLinkCopied(false); // reset tooltip text
        }, 1000);
    };

    const content = (
        <Box pad="xsmall" {...contentProps}>
            {isLoading ? (<Loading />) : children }
        </Box>
    );

    return (
        <Box
            margin={isCollapsed ?  { top: 'medium' }: { vertical: 'medium' }}
            id={ anchorId || autoGeneratedId}
            round="xsmall"
            flex={false}
            {...rest}
        >
            <Box
                border={(title || control) && !hideBorder && !isCollapsed ? {side: 'bottom', color: 'border'} : false}
                flex={false}
                {...headerProps}
                fill="horizontal">
                <Box direction="row"
                    onMouseEnter={() => {setShowCopyLink(true)}}
                    onMouseLeave={() => {setShowCopyLink(false); setIsLinkCopied(false);}}
                >
                    {(title || customIcon)&&
                    <Box 
                        justify="start" 
                        direction="row" 
                        gap="xsmall" 
                        align="center" 
                        margin={{bottom: subheader ? 'small' : '0'}} 
                        {...titleContainerProps}>
                        {leftControl}
                        {icon && (
                            <Box>
                                <FontAwesomeIcon SelectedIcon={icon} highlight="accent" color="text" size="medium" {...iconProps} />
                            </Box>
                        )}
                        {customIcon}
                        <Box direction="column">
                            {title && (
                                <Heading
                                    responsive={false}
                                    align="start"
                                    level={level}
                                    size="medium"
                                    margin={{horizontal: 'xsmall', vertical: subheader ? 'xxsmall' : 'small'}}
                                    {...titleProps}>
                                    <Box direction="row" gap="small">
                                        {collapsible ? (
                                            <Button
                                                event={{section: `${snakeCase(title)}_section`, element: 'collapse'}}
                                                onClick={() => setIsCollapsed(!isCollapsed)} 
                                                hoverIndicator 
                                                plain>
                                                <Box align="center" direction="row" gap="small">
                                                    <Text size="large">{title}</Text>
                                                    <Box>
                                                        <FontAwesomeIcon SelectedIcon={IconArrowDown} size="xxsmall" rotation={isCollapsed ? 90 : 0}/>
                                                    </Box>
                                                </Box>
                                            </Button>
                                        ) : title}
                                        { !hideDeepLink && showCopyLink && autoGeneratedId &&
                                            <Button
                                                event={{section: `${snakeCase(title)}_section`, element: 'copy_section_link'}}  
                                                icon={<FontAwesomeIcon SelectedIcon={IconLink} size="xsmall" color="text-weak" />}
                                                onClick={copyLink}
                                                tip={isLinkCopied ? "Copied!" : "Copy link"}
                                                size="small"
                                                pad="small"
                                                hoverIndicator
                                                plain
                                            />
                                        }
                                    </Box>
                                </Heading>
                            )}
                            {subheader && (<Text {...subheaderProps}>{subheader}</Text>)}
                        </Box>
                    </Box>}
                    {control && !fullWidthControls && <Box margin={{ bottom: 'xxsmall' }} justify="center" align="end" flex {...controlProps}>{control}</Box>}
                </Box>
                { control && fullWidthControls && !isCollapsed && <Box margin={{ bottom: 'xxsmall' }} justify="center" align="end" flex {...controlProps}>{control}</Box>}
            </Box>
            {/*
                Only wrapped in a Collapsible when it can actually collapse. Grommet's
                Collapsible renders a Box that sizes to its content and forwards no props,
                so inside a height-constrained parent (a Drawer) it refuses to shrink below
                that content: whatever sits under it never gets a bounded height, so
                `overflow: auto` never scrolls and anything shrinkable gets squashed instead.
            */}
            {(children || isLoading) ?
                ((collapsible || collapsedByDefault)
                    ? (<Collapsible open={!isCollapsed}>{content}</Collapsible>)
                    : content):
                (<></>)
            }
        </Box>
    )
};

export default Section;
